import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/rootReducer';
import HSTable from '../../../../../common/HSTable';
import { Reporting, ReportingObligation } from '../../../redux/types';
import moment from 'moment';
//import { getReportColor } from '../../../utils';
import { GrFormNext } from 'react-icons/gr';
import ReportingObligationDoc from './ReportingObligationDoc';
import { useLocation } from 'react-router';

type Props = {
    setSelectedType: (s?: string) => void;
    showDate: boolean;
    setDate: (b: boolean) => void;
};

type LocationType = {
    date: string | undefined;
};

const ReportingObligations: React.FC<Props> = ({ setSelectedType, showDate, setDate }) => {
    const location = useLocation();
    const loading = useSelector((state: RootState) => state.investments.rLoading);
    const reportingObligations = useSelector((state: RootState) => state.investments.reportingObligations);
    const bookingUUID = useSelector((state: RootState) => state.investments.investmentDetails?.bookingUUID);

    const [selectedObligation, setObligation] = useState<ReportingObligation | undefined>(undefined);
    const stateData: { date: string | undefined } = location && (location.state as LocationType);

    const reportingObligationList = useMemo(() => {
        if (bookingUUID) {
            let res: ReportingObligation[] = [];
            reportingObligations.forEach((r) => {
                if (r.reports && r.reports[0] && r.reports[0].bookingUUID === bookingUUID) {
                    res.push(r);
                }
            });
            return res;
        }
        return reportingObligations;
    }, [bookingUUID, reportingObligations]);

    useEffect(() => {
        if (stateData?.date && showDate) {
            const obligation = reportingObligationList.find((r) => r.obligationDate?.date === stateData.date);
            setObligation(obligation);
        }
    }, [stateData?.date, reportingObligationList, showDate]);

    const getCountOfFiles = (items: Reporting[]) => {
        let count = 0;
        [...items]
            .filter((a, i, self) => self.findIndex((s) => s.obligationType === a.obligationType) === i)
            .forEach((i) => {
                count = count + (i.uploadDetails || []).length;
            });
        return count;
    };

    const columns = [
        {
            dataIndex: 'obligationDate',
            title: 'Date',
            width: 100,
            render: (text?: { date: string }) => <p>{moment(text?.date).format('DD-MMM-YYYY')}</p>,
            sorter: (a: ReportingObligation, b: ReportingObligation) =>
                moment(a.obligationDate?.date).unix() - moment(b.obligationDate?.date).unix(),
        },
        {
            dataIndex: 'reports',
            title: 'Document Type',
            width: 200,
            render: (text: Reporting[]) => (
                <>
                    <p className={`whitespace-pre-wrap`}>
                        {[...text]
                            .filter((a, i, self) => self.findIndex((s) => s.obligationType === a.obligationType) === i)
                            ?.map((t, index) => (
                                <span
                                    // className={getReportColor(t.uploadDetails || [], row.obligationDate?.date || '')}
                                    key={`${index}_${t.obligationType}`}
                                >
                                    {index === 0 ? `${t.obligationType}` : `, ${t.obligationType}`}
                                </span>
                            ))}
                    </p>
                </>
            ),
        },
        {
            dataIndex: 'files',
            title: 'Files',
            width: 200,
            render: (text: string, row: ReportingObligation) => (
                <p className={text || ''}>{getCountOfFiles(row.reports || [])}</p>
            ),
            sorter: (a: ReportingObligation, b: ReportingObligation) =>
                getCountOfFiles(a.reports || []) - getCountOfFiles(b.reports || []),
        },
    ];

    return (
        <div className="px-6 pb-4">
            <div>
                <nav className="py-1 pt-2 px-2 lg:px-4" aria-label="Breadcrumb" style={{ backgroundColor: '#F4F7FD' }}>
                    <li className="inline-flex items-center">
                        <span
                            className="inline-flex mr-1 items-center text-sm font-semibold my-1.5 common_read_title cursor-pointer relative"
                            style={{ top: '-4px', color: '#404040' }}
                            onClick={() => {
                                setSelectedType(undefined);
                                setDate(false);
                            }}
                        >
                            Documents
                        </span>
                        <span
                            className="inline-flex items-center text-sm font-semibold my-1.5 common_read_title cursor-pointer relative"
                            style={{ top: '-4px', color: '#6E809D' }}
                        >
                            <GrFormNext
                                className="mr-1 md:mr-1 relative"
                                color="#26AE60"
                                size="1.2rem"
                                style={{ top: '1px' }}
                            />
                        </span>
                    </li>
                    <li className="inline-flex items-center ml-1">
                        <span
                            className="inline-flex items-center text-[#B1C1D4] text-sm font-semibold my-1.5 common_read_title cursor-pointer relative"
                            style={{ top: '-4px' }}
                            onClick={() => {
                                setObligation(undefined);
                                setDate(false);
                            }}
                        >
                            Reporting Obligation
                        </span>
                        {selectedObligation && (
                            <span
                                className="inline-flex items-center text-sm font-semibold my-1.5 common_read_title cursor-pointer relative"
                                style={{ top: '-4px', color: '#6E809D' }}
                            >
                                <GrFormNext
                                    className="mr-1 md:mr-1 relative"
                                    color="#26AE60"
                                    size="1.2rem"
                                    style={{ top: '1px' }}
                                />
                            </span>
                        )}
                    </li>
                    {selectedObligation && (
                        <li className="inline-flex items-center ml-1">
                            <span
                                className="inline-flex text-[#B1C1D4] items-center text-sm font-semibold my-1.5 common_read_title  relative"
                                style={{ top: '-4px' }}
                            >
                                {moment(selectedObligation.obligationDate?.date).format('DD/MM/YYYY')}
                            </span>
                        </li>
                    )}
                </nav>
            </div>
            {!selectedObligation && (
                <HSTable
                    columns={columns}
                    loading={loading}
                    data={reportingObligationList}
                    rowKey={(d: ReportingObligation) => `${d.obligationDate?.date}_${new Date().getMilliseconds()}`}
                    onRowClick={(d: ReportingObligation) => setObligation(d)}
                />
            )}
            {selectedObligation?.obligationDate?.date && (
                <ReportingObligationDoc
                    date={selectedObligation.obligationDate.date}
                    reports={[...(selectedObligation.reports || [])]}
                />
            )}
        </div>
    );
};

export default ReportingObligations;
